import { useTranslation } from 'react-i18next';

import { appSectionMode, settingString, useSettings } from '../../lib/settings';

/**
 * Mobile-app section — spec §7.1 ("HikeSJ + store badges"), governed by
 * `settings.app_section_mode` (§6.18, §27.8):
 *
 *   hidden       renders NOTHING (the default). Both store listings are currently delisted,
 *                so shipping badges would point at nothing on the busiest page of the site.
 *   coming_soon  the section with a "coming soon" chip instead of store badges.
 *   live         real App Store / Google Play links from settings.
 *
 * Staff flip this in the admin — no deploy. A failed settings fetch falls back to `hidden`,
 * so the failure mode is a missing section, never a broken link.
 */
export function MobileAppSection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const mode = appSectionMode(settings);

  if (mode === 'hidden') return null;

  const iosUrl = settingString(settings, 'app.ios_url');
  const androidUrl = settingString(settings, 'app.android_url');

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid items-center gap-8 overflow-hidden rounded-3xl border border-line bg-panel p-8 shadow-soft sm:p-12 md:grid-cols-2 dark:shadow-soft-dark">
        <div>
          <h2 className="text-2xl font-bold text-content sm:text-3xl">{t('home.appTitle')}</h2>
          <p className="mt-3 max-w-md leading-relaxed text-content-muted">{t('home.appBody')}</p>

          {mode === 'live' ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <StoreBadge top={t('home.appStoreTop')} bottom={t('home.appStoreName')} icon="" href={iosUrl} />
              <StoreBadge top={t('home.appStoreTop')} bottom={t('home.playStoreName')} icon="▶" href={androidUrl} />
            </div>
          ) : (
            <p className="mt-6 inline-block rounded-pill bg-content/5 px-4 py-2 text-sm font-medium text-content-muted">
              {t('home.appComingSoon')}
            </p>
          )}
        </div>

        {/* Decorative phone mockups (§20.2 default — no asset supplied). */}
        <div aria-hidden="true" className="flex justify-center gap-4">
          <PhoneFrame className="translate-y-4" />
          <PhoneFrame className="hidden sm:block" />
        </div>
      </div>
    </section>
  );
}

/**
 * Only rendered in `live` mode. A blank URL still cannot produce a dead link: without an href
 * this degrades to a dimmed span rather than an <a> pointing nowhere.
 */
function StoreBadge({ top, bottom, icon, href }: { top: string; bottom: string; icon: string; href: string }) {
  const inner = (
    <>
      <span className="text-2xl leading-none" aria-hidden="true">{icon || ''}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-white/70">{top}</span>
        <span className="text-sm font-semibold">{bottom}</span>
      </span>
    </>
  );
  const className =
    'inline-flex items-center gap-3 rounded-2xl bg-ink px-5 py-2.5 text-left text-white transition-opacity hover:opacity-90';

  if (!href) {
    return <span className={`${className} opacity-50`}>{inner}</span>;
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {inner}
    </a>
  );
}

function PhoneFrame({ className = '' }: { className?: string }) {
  return (
    <div className={`h-72 w-36 rounded-[2rem] border-4 border-ink bg-canvas p-2 shadow-soft ${className}`}>
      <div className="relative h-full w-full overflow-hidden rounded-[1.5rem] bg-content/5">
        <div className="absolute left-1/2 top-2 h-1.5 w-12 -translate-x-1/2 rounded-pill bg-ink/30" />
        <div className="mt-8 space-y-2 p-3">
          <div className="h-16 w-full rounded-lg bg-content/10" />
          <div className="h-3 w-2/3 rounded bg-content/10" />
          <div className="h-3 w-1/2 rounded bg-content/10" />
          <div className="mt-3 h-16 w-full rounded-lg bg-content/10" />
        </div>
      </div>
    </div>
  );
}
